import React from "react";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import Login from "../pages/Auth/login";
import Layout from "../components/Layout";
import Dashboard from "../pages/Dashboard";
import UserManagement, { AddUser, EditUser } from "../pages/UserManagement";
import IFCProcessing from "../pages/IFCProcessing";
import ProjectManagement from "../pages/ProjectManagement";
import AddProjectPage from "../pages/AddProjectPage";
import ProjectFormPage from "../pages/ProjectManagement/ProjectFormPage";
import ViewProjectPage from "../pages/ViewProjectPage";
import Help from "../pages/Help";
import Reports from "../pages/Reports";
import TrackAudits from "../pages/TrackAudits";
import UploadSuccess from "../pages/UploadSuccess";
import ManageAccount from "../pages/ManageAccount";
import { ProtectedRoute } from "./protectedRoute";
import { TypeRestrictedRoute } from "./typeRestrictedRoute";
import { IdleTimeoutWrapper } from "../components/IdleTimeoutWrapper";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const protectedRootRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_protected",
  component: () => (
    <ProtectedRoute>
      <IdleTimeoutWrapper>
        <Layout>
          <Outlet />
        </Layout>
      </IdleTimeoutWrapper>
    </ProtectedRoute>
  ),
});

const indexRedirectRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/",
  component: () => <Navigate to="/dashboard" replace />,
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/dashboard",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <Dashboard />
    </TypeRestrictedRoute>
  ),
});

const userManagementRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/user-management",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0"]}>
      <UserManagement />
    </TypeRestrictedRoute>
  ),
});

const userAddRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/user-management/add",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0"]}>
      <AddUser />
    </TypeRestrictedRoute>
  ),
});

const userEditRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/user-management/edit/$id",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0"]}>
      <EditUser />
    </TypeRestrictedRoute>
  ),
});

const ifcProcessingRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/ifc-processing",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <IFCProcessing />
    </TypeRestrictedRoute>
  ),
});

const ifcProcessingIdRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/ifc-processing/$id",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <IFCProcessing />
    </TypeRestrictedRoute>
  ),
});

const projectManagementRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/project-management",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <ProjectManagement />
    </TypeRestrictedRoute>
  ),
});

const projectAddRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/project-management/add",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <AddProjectPage />
    </TypeRestrictedRoute>
  ),
});

const projectEditRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/project-management/edit/$id",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <ProjectFormPage />
    </TypeRestrictedRoute>
  ),
});

const projectViewRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/project-management/view/$id",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <ViewProjectPage />
    </TypeRestrictedRoute>
  ),
});

const helpRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/help",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <Help />
    </TypeRestrictedRoute>
  ),
});

const reportsRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/reports",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <Reports />
    </TypeRestrictedRoute>
  ),
});

const trackAuditsRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/track-audits",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <TrackAudits />
    </TypeRestrictedRoute>
  ),
});

const uploadSuccessRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/upload-success/$projectId",
  component: () => <UploadSuccess />,
});

const uploadSuccessFallbackRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/upload-success",
  component: () => <Navigate to="/project-management" replace />,
});

const manageAccountRoute = createRoute({
  getParentRoute: () => protectedRootRoute,
  path: "/manage-account",
  component: () => (
    <TypeRestrictedRoute allowedTypes={["0", "2"]}>
      <ManageAccount />
    </TypeRestrictedRoute>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => <Login />,
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "*",
  component: () => <Navigate to="/login" replace />,
});

const routeTree = rootRoute.addChildren([
  protectedRootRoute,
  indexRedirectRoute,
  dashboardRoute,
  userManagementRoute,
  userAddRoute,
  userEditRoute,
  ifcProcessingRoute,
  ifcProcessingIdRoute,
  projectManagementRoute,
  projectAddRoute,
  projectEditRoute,
  projectViewRoute,
  helpRoute,
  reportsRoute,
  trackAuditsRoute,
  uploadSuccessRoute,
  uploadSuccessFallbackRoute,
  manageAccountRoute,
  loginRoute,
  notFoundRoute,
]);

export const router = createRouter({
  routeTree,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  // Allow passing arbitrary navigation state (used throughout the app)
  interface HistoryState {
    from?: unknown;
    project?: unknown;
    user?: unknown;
    jobId?: string | null;
    jobIds?: string[] | null;
    status?: string | null;
    output?: string | null;
  }
}

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};

